import { useState, useEffect, useRef } from 'react';
import type { SuggestionItem } from '../../constants/suggestions';

type SuggestionListProps = {
  suggestions: SuggestionItem[];
  userInput: string;
  onSelect: (suggestion: SuggestionItem) => void;
  onClose: () => void;
};

const SuggestionList = ({ suggestions, userInput, onSelect, onClose }: SuggestionListProps) => {
  const [filtered, setFiltered] = useState<SuggestionItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const term = (userInput || '').trim().toLowerCase();
    const matches = (suggestions || [])
      .filter((s) => s?.label?.toLowerCase().includes(term));
    setFiltered(matches);
    setActiveIndex(0);
  }, [suggestions, userInput]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    if (!containerRef.current) return;
    const active = containerRef.current.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIndex]) {
        onSelect(filtered[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleSuggestionMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  const handleContainerMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  if (!filtered.length) return null;

  return (
    <div
      ref={containerRef}
      className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-y-auto max-h-64 z-50"
      onKeyDown={handleKeyDown}
      onMouseDown={handleContainerMouseDown}
      role="listbox"
    >
      {filtered.map((item, index) => (
        <button
          key={item.id}
          type="button"
          role="option"
          data-active={index === activeIndex}
          aria-selected={index === activeIndex}
          onMouseEnter={() => setActiveIndex(index)}
          onMouseDown={handleSuggestionMouseDown}
          onClick={() => onSelect(item)}
          className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
            index === activeIndex
              ? 'bg-[#F4EFFF] text-[#6E3EF5]'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
};

export default SuggestionList;
